// US state long-name → 2-letter abbreviation. Used as a fallback when Google
// Places returns `administrative_area_level_1` with a full state name but no
// short_name (which happens on some iPad Safari results).
export const US_STATE_TO_ABBR = {
    "alabama": "AL", "alaska": "AK", "arizona": "AZ", "arkansas": "AR",
    "california": "CA", "colorado": "CO", "connecticut": "CT", "delaware": "DE",
    "district of columbia": "DC", "florida": "FL", "georgia": "GA",
    "hawaii": "HI", "idaho": "ID", "illinois": "IL", "indiana": "IN",
    "iowa": "IA", "kansas": "KS", "kentucky": "KY", "louisiana": "LA",
    "maine": "ME", "maryland": "MD", "massachusetts": "MA", "michigan": "MI",
    "minnesota": "MN", "mississippi": "MS", "missouri": "MO", "montana": "MT",
    "nebraska": "NE", "nevada": "NV", "new hampshire": "NH", "new jersey": "NJ",
    "new mexico": "NM", "new york": "NY", "north carolina": "NC",
    "north dakota": "ND", "ohio": "OH", "oklahoma": "OK", "oregon": "OR",
    "pennsylvania": "PA", "rhode island": "RI", "south carolina": "SC",
    "south dakota": "SD", "tennessee": "TN", "texas": "TX", "utah": "UT",
    "vermont": "VT", "virginia": "VA", "washington": "WA", "west virginia": "WV",
    "wisconsin": "WI", "wyoming": "WY",
    // Territories
    "puerto rico": "PR", "guam": "GU", "american samoa": "AS",
    "u.s. virgin islands": "VI", "northern mariana islands": "MP",
};

// ZIP-prefix → state fallback. Uses the first 3 digits of the US ZIP (ZIP-3)
// which are contiguous by state. Covers >99% of US mail. Not exhaustive for
// every 3-digit block (some prefixes span two states) — those hits return an
// empty string so the user can still type manually.
const ZIP3_TO_STATE = {};
const ZIP_RANGES = [
    // AL
    ["AL", 350, 369],
    // AK
    ["AK", 995, 999],
    // AZ
    ["AZ", 850, 865],
    // AR
    ["AR", 716, 729], ["AR", 755, 755],
    // CA
    ["CA", 900, 966],
    // CO
    ["CO", 800, 816],
    // CT
    ["CT", 60, 69],
    // DE
    ["DE", 197, 199],
    // DC
    ["DC", 200, 205], ["DC", 569, 569],
    // FL
    ["FL", 320, 349],
    // GA
    ["GA", 300, 319], ["GA", 398, 399],
    // HI
    ["HI", 967, 968],
    // ID
    ["ID", 832, 838],
    // IL
    ["IL", 600, 629],
    // IN
    ["IN", 460, 479],
    // IA
    ["IA", 500, 528],
    // KS
    ["KS", 660, 679],
    // KY
    ["KY", 400, 427],
    // LA
    ["LA", 700, 714],
    // ME
    ["ME", 39, 49],
    // MD
    ["MD", 206, 219],
    // MA
    ["MA", 10, 27], ["MA", 55, 55],
    // MI
    ["MI", 480, 499],
    // MN
    ["MN", 550, 567],
    // MS
    ["MS", 386, 397],
    // MO
    ["MO", 630, 658],
    // MT
    ["MT", 590, 599],
    // NE
    ["NE", 680, 693],
    // NV
    ["NV", 889, 898],
    // NH
    ["NH", 30, 38],
    // NJ
    ["NJ", 70, 89],
    // NM
    ["NM", 870, 884],
    // NY
    ["NY", 100, 149], ["NY", 63, 63],
    // NC
    ["NC", 270, 289],
    // ND
    ["ND", 580, 588],
    // OH
    ["OH", 430, 458],
    // OK
    ["OK", 730, 749],
    // OR
    ["OR", 970, 979],
    // PA
    ["PA", 150, 196],
    // PR
    ["PR", 6, 9],
    // RI
    ["RI", 28, 29],
    // SC
    ["SC", 290, 299],
    // SD
    ["SD", 570, 577],
    // TN
    ["TN", 370, 385],
    // TX
    ["TX", 750, 799], ["TX", 733, 733], ["TX", 885, 885],
    // UT
    ["UT", 840, 847],
    // VT
    ["VT", 50, 59],
    // VA
    ["VA", 220, 246],
    // WA
    ["WA", 980, 994],
    // WV
    ["WV", 247, 268],
    // WI
    ["WI", 530, 549],
    // WY
    ["WY", 820, 831],
];
ZIP_RANGES.forEach(([st, lo, hi]) => {
    for (let i = lo; i <= hi; i += 1) {
        const key = String(i).padStart(3, "0");
        ZIP3_TO_STATE[key] = st;
    }
});

/**
 * Best-effort resolver for the 2-letter US state code. Priority:
 *   1. Google Places `administrative_area_level_1.short_name`.
 *   2. Google Places `administrative_area_level_1.long_name` → abbrev.
 *   3. ZIP-3 fallback lookup.
 *   4. Empty string (user types manually).
 */
export function resolveUsState({ shortName, longName, postalCode }) {
    if (shortName && shortName.length === 2) return shortName.toUpperCase();
    if (longName) {
        const abbr = US_STATE_TO_ABBR[longName.toLowerCase().trim()];
        if (abbr) return abbr;
    }
    if (postalCode) {
        const prefix = String(postalCode).replace(/\D/g, "").slice(0, 3);
        if (prefix.length === 3 && ZIP3_TO_STATE[prefix]) return ZIP3_TO_STATE[prefix];
    }
    return "";
}
